import {
	AsyncSelect,
	AsyncSelectProps,
	Button,
	Divider,
	Dropdown,
	Icons,
	Inline,
	Stack,
	Table,
	Text,
	Time,
} from "@sembark-travel/ui/base"
import {
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import * as Validator from "yup"
import { ListView, Search, useSearch } from "@sembark-travel/ui/list"
import {
	IListResponse,
	ISimpleListResponse,
	useXHR,
	XHRInstance,
} from "@sembark-travel/xhr"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import { ITripSourceTag } from "./store"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { Breadcrumbs, Link } from "@sembark-travel/ui/router"
import { generatePath } from "../router-utils"
import { useState } from "react"
import { Optional } from "utility-types"

function XHR(xhr: XHRInstance) {
	return {
		async getTag(
			params: unknown
		): Promise<ISimpleListResponse<ITripSourceTag>> {
			return xhr.get("/trip-source-tags", { params }).then((res) => res.data)
		},
		async getTagWithPagination(
			params?: Record<string, unknown>
		): Promise<IListResponse<ITripSourceTag>> {
			return xhr
				.get("/trip-source-tags", { params: { ...params, pagination: "page" } })
				.then((res) => res.data)
		},
		async storeTag(params: unknown): Promise<ITripSourceTag> {
			return xhr.post("/trip-source-tags", params).then((res) => res.data.data)
		},
		async editTag(
			Id: number | string,
			params: unknown
		): Promise<ITripSourceTag> {
			return xhr
				.patch(`/trip-source-tags/${Id}`, params)
				.then((res) => res.data.data)
		},
		async editTripSourceTags(
			tripSourceId: number | string,
			params: unknown
		): Promise<ITripSourceTag> {
			return xhr
				.post(`trip-sources/${tripSourceId}/tags`, params)
				.then((res) => res.data.data)
		},
		async destroyTag(id: number | string) {
			return xhr
				.delete<{ message: string }>(`/trip-source-tags/${id}`)
				.then((res) => res.data)
		},
	}
}
export function ListTags() {
	const [params, setParams] = useSearch()
	const [refreshId, setRefreshId] = useState<number>(0)
	return (
		<>
			<Breadcrumbs
				title="Tags"
				items={[
					[generatePath("/org/trip-sources"), "Trip Sources"],
					["", "Tags"],
				]}
			/>
			<Search
				title={"Trip Source Tags"}
				initialParams={params}
				onSearch={(params) => setParams({ ...params, page: 1 })}
				actions={
					<SourceTagDialogBox
						onSuccess={() => {
							setRefreshId((id) => id + 1)
						}}
					>
						{({ create }) => <Button onClick={create}>New Tag </Button>}
					</SourceTagDialogBox>
				}
			>
				<ListView
					key={refreshId}
					params={params}
					pageKey={`trip-source-tags`}
					fetch={(xhr, params) =>
						XHR(xhr).getTagWithPagination({
							...params,
							include: "created_by,trip_source_counts",
						})
					}
					onPageChange={(page) => setParams({ ...params, page })}
				>
					{({ items, refresh, xhr }) => (
						<Table
							headers={["Name", "Source Count", "Created By", ""]}
							bordered
							striped
							alignCols={{ 3: "right" }}
							rows={items.map((item) => [
								item.name,
								item.trip_source_count ? (
									<Link
										to={generatePath("/org/trip-sources")}
										query={{
											tags: [item.id + "__" + item.name],
										}}
									>
										{item.trip_source_count}
									</Link>
								) : (
									0
								),
								<Stack gap="px">
									<Text>{item.created_by?.name}</Text>
									<Text color="muted" fontSize="sm">
										<Time timestamp={item.created_at} />
									</Text>
								</Stack>,
								<Dropdown alignRight>
									<Dropdown.ToggleButton level="tertiary" size="sm">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<Dropdown.MenuItem
											onClick={() => {
												if (
													window.confirm(
														"Are you sure you want to delete this Tag ?."
													)
												) {
													XHR(xhr)
														.destroyTag(item.id)
														.then((resp) => {
															showSnackbar(
																resp.message ||
																	"Trip Source Tag  deleted successfully."
															)
															refresh()
														})
														.catch((e) => {
															const error: Error = e
															window.alert(
																error.message || "Something went wrong"
															)
														})
												}
											}}
										>
											<Icons.Trash /> Delete
										</Dropdown.MenuItem>
										<EditTagDialogBox
											tag={item}
											onSuccess={() => {
												setRefreshId((id) => id + 1)
											}}
										>
											{({ create }) => (
												<Dropdown.MenuItem onClick={create}>
													<Icons.Pencil /> Edit
												</Dropdown.MenuItem>
											)}
										</EditTagDialogBox>
									</Dropdown.Menu>
								</Dropdown>,
							])}
						/>
					)}
				</ListView>
			</Search>
		</>
	)
}

export function EditTagDialogBox({
	tag,
	onSuccess,
	children,
}: {
	tag: ITripSourceTag
	onSuccess: () => void
	children: (props: { create: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children?.({ create: open })}
			<Dialog open={isOpen} onClose={close} title="Edit Tag" sm>
				<Dialog.Body>
					<TripSourceTagForm
						name={tag.name}
						onSuccess={async (value: { name: string }) => {
							await XHR(xhr).editTag(tag.id, value)
							onSuccess()
							close()
						}}
						onCancel={() => {
							close()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
/**
 * Creating New trip source tag through the Action Button
 */
export function SourceTagDialogBox({
	onSuccess,
	children,
}: {
	onSuccess: () => void
	children: (props: { create: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children?.({ create: open })}
			<Dialog open={isOpen} onClose={close} title="Create Tag" sm>
				<Dialog.Body>
					<TripSourceTagForm
						onSuccess={async (value: { name: string }) => {
							await XHR(xhr).storeTag(value)
							onSuccess()
							close()
						}}
						onCancel={() => {
							close()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

const tagValidation = validateFormValues(
	Validator.object().shape({
		name: Validator.string()
			.required("Please provide the Tag")
			.min(1, "At least one character is required.")
			.max(10, "Limit: less than 10 characters."),
	})
)
export function TripSourceTagForm({
	name,
	onCancel,
	onSuccess,
}: {
	name?: string
	onCancel: () => void
	onSuccess: (value: { name: string }) => Promise<void>
}) {
	const initialValues = { name: name || "" }
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			validate={tagValidation}
			onSubmit={withServerErrors(async (value) => {
				await onSuccess(value)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<TextInputField
							label="Tag Name"
							name="name"
							required
							type="text"
							placeholder="e.g. Premium"
							maxWidth="xs"
						/>
						<Divider marginY={"0"} />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save"}
							</Button>
							<Button disabled={submitting} onClick={onCancel} level="tertiary">
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
/**
 * Creating New trip source tag through the select input
 */

export function SelectTripSourceTags({
	onCreateSuccess,
	...props
}: { onCreateSuccess?: (tripSourceTag: ITripSourceTag) => void } & Omit<
	AsyncSelectProps,
	"fetch"
>) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			creatable
			placeholder="Select Tags..."
			{...props}
			fetch={(q) =>
				XHR(xhr)
					.getTag({
						q,
					})
					.then((resp) => resp.data)
			}
		/>
	)
}

/**
 * Edit Trip Source Tag for Agent/Company
 */
export function EditTripSourceTags({
	sourceId,
	tags,
	onCancel,
	onSuccess,
}: EditTagsProps) {
	const xhr = useXHR()
	const initialValues = { items: [sourceId], tags: tags }
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				const tags = (values.tags || []).map((t) => t.name)
				await XHR(xhr).editTripSourceTags(sourceId, { tags })
				onSuccess()
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<SelectField
							select={SelectTripSourceTags}
							name="tags"
							fetchOnMount
							label="Tag Name"
							placeholder="e.g Premium"
							creatable
							maxWidth={"xs"}
							multiple
						/>
						<SubmissionError />
						<Divider marginY={"0"} />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save"}
							</Button>
							<Button disabled={submitting} onClick={onCancel} level="tertiary">
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export interface EditTagsProps {
	tags?: ITripSourceTag[]
	sourceId: number
	onCancel: () => void
	onSuccess: () => void
}

export function EditTripSourceTagsDialog({
	children,
	...props
}: Optional<EditTagsProps, "onSuccess" | "onCancel"> & {
	children: (props: { onEdit: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ onEdit: open })}
			<Dialog open={isOpen} onClose={close} title="Edit Trip Source Tags" sm>
				<Dialog.Body>
					<EditTripSourceTags
						{...props}
						onSuccess={() => {
							close()
							props.onSuccess?.()
						}}
						onCancel={() => {
							close()
							props.onCancel?.()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
